import { useState, useEffect, type FormEvent } from 'react';
import type {
  TenantSubscription,
  BillingPlan,
  BillingCycle,
  SubscriptionStatus,
  CreateSubscriptionDto,
  UpdateSubscriptionDto,
} from '@yawi/types';
import { Button, Input, Select, Textarea, Modal } from '@/core/ui/components';
import { useServices } from '@/app/providers';

interface TenantSubscriptionModalProps {
  subscription?: TenantSubscription | null;
  onClose: () => void;
  onSave: (dto: CreateSubscriptionDto | UpdateSubscriptionDto) => Promise<void>;
}

export function TenantSubscriptionModal({ subscription, onClose, onSave }: TenantSubscriptionModalProps) {
  const { billingRepository } = useServices();
  const isEdit = !!subscription;

  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(true);

  const [planId, setPlanId] = useState(subscription?.planId || '');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>(subscription?.billingCycle || 'MONTHLY');
  const [status, setStatus] = useState<SubscriptionStatus>(subscription?.status || 'ACTIVE');
  const [customUnitsLimit, setCustomUnitsLimit] = useState<string>(subscription?.customUnitsLimit?.toString() || '');
  const [customUsersLimit, setCustomUsersLimit] = useState<string>(subscription?.customUsersLimit?.toString() || '');
  const [customStorageLimit, setCustomStorageLimit] = useState<string>(subscription?.customStorageLimit?.toString() || '');
  const [notes, setNotes] = useState(subscription?.notes || '');

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const result = await billingRepository.getAllPlans({ page: 1, limit: 100 });
        setPlans(result.data);
      } catch {
        // error handled
      } finally {
        setPlansLoading(false);
      }
    };
    loadPlans();
  }, [billingRepository]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!planId) return;

    setIsLoading(true);
    try {
      if (isEdit) {
        const dto: UpdateSubscriptionDto = {
          planId,
          status,
          billingCycle,
          customUnitsLimit: customUnitsLimit ? parseInt(customUnitsLimit) : undefined,
          customUsersLimit: customUsersLimit ? parseInt(customUsersLimit) : undefined,
          customStorageLimit: customStorageLimit ? parseInt(customStorageLimit) : undefined,
          notes: notes || undefined,
        };
        await onSave(dto);
      } else {
        const dto: CreateSubscriptionDto = {
          planId,
          billingCycle,
          customUnitsLimit: customUnitsLimit ? parseInt(customUnitsLimit) : undefined,
          customUsersLimit: customUsersLimit ? parseInt(customUsersLimit) : undefined,
          customStorageLimit: customStorageLimit ? parseInt(customStorageLimit) : undefined,
          notes: notes || undefined,
        };
        await onSave(dto);
      }
      onClose();
    } catch {
      // error handled by parent
    } finally {
      setIsLoading(false);
    }
  };

  const planOptions = plans.map((p) => ({ value: p.id, label: `${p.name} (${p.code})` }));

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Editar Suscripción' : 'Crear Suscripción'}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} isLoading={isLoading} disabled={!planId || plansLoading}>
            {isEdit ? 'Guardar' : 'Crear'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <Select
          label="Plan"
          value={planId}
          onChange={(e) => setPlanId(e.target.value)}
          options={[{ value: '', label: 'Seleccionar plan...' }, ...planOptions]}
          disabled={plansLoading}
        />

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Ciclo de Facturación"
            value={billingCycle}
            onChange={(e) => setBillingCycle(e.target.value as BillingCycle)}
            options={[
              { value: 'MONTHLY', label: 'Mensual' },
              { value: 'YEARLY', label: 'Anual' },
            ]}
          />

          {isEdit && (
            <Select
              label="Estado"
              value={status}
              onChange={(e) => setStatus(e.target.value as SubscriptionStatus)}
              options={[
                { value: 'TRIAL', label: 'Prueba' },
                { value: 'ACTIVE', label: 'Activo' },
                { value: 'PAST_DUE', label: 'Pago pendiente' },
                { value: 'SUSPENDED', label: 'Suspendido' },
                { value: 'CANCELLED', label: 'Cancelado' },
              ]}
            />
          )}
        </div>

        <div className="border-t border-theme pt-4">
          <h4 className="text-sm font-medium text-theme mb-3">Límites Personalizados (opcionales)</h4>
          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Unidades"
              type="number"
              min={0}
              value={customUnitsLimit}
              onChange={(e) => setCustomUnitsLimit(e.target.value)}
              placeholder="Plan default"
            />
            <Input
              label="Usuarios"
              type="number"
              min={0}
              value={customUsersLimit}
              onChange={(e) => setCustomUsersLimit(e.target.value)}
              placeholder="Plan default"
            />
            <Input
              label="Storage (bytes)"
              type="number"
              min={0}
              value={customStorageLimit}
              onChange={(e) => setCustomStorageLimit(e.target.value)}
              placeholder="Plan default"
            />
          </div>
        </div>

        <Textarea
          label="Notas"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notas internas sobre esta suscripción..."
        />
      </form>
    </Modal>
  );
}
